/**
 * Templates management page — Phase 1.8 Pass 5.
 *
 * Lists keepa_query_templates rows + actions:
 *   - Run     → opens search page with this template preselected (via querystring)
 *   - Clone   → POST /:id/clone
 *   - Archive → POST /:id/archive  (built-ins included; restore not in v1)
 *   - Edit    → modal w/ name + description + JSON fixedFilters + JSON parameters
 *   - New     → same modal but POST
 *
 * v1 JSON editor is deliberately raw — visual builder is Phase 2.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Archive, ChevronLeft, Copy, Edit2, Lock, Play } from 'lucide-react';
import type { KeepaQueryTemplate } from '@sourcito/schemas';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '../design';
import {
  useArchiveKeepaTemplate,
  useCloneKeepaTemplate,
  useCreateKeepaTemplate,
  useKeepaTemplates,
  useUpdateKeepaTemplate,
} from '../lib/keepaTemplates';

type ModalMode = { kind: 'create' } | { kind: 'edit'; template: KeepaQueryTemplate } | null;

export function TemplatesPage() {
  const [modal, setModal] = useState<ModalMode>(null);
  const { data, isLoading, isError, error } = useKeepaTemplates();
  const clone = useCloneKeepaTemplate();
  const archive = useArchiveKeepaTemplate();

  const rows = data ?? [];

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/search"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight">Templates</h1>
              <p className="text-xs text-fg-tertiary">
                Built-in + custom Keepa query templates with per-run swappable parameters.
              </p>
            </div>
          </div>
          <Button size="sm" onClick={() => setModal({ kind: 'create' })}>
            New template
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-10 space-y-4">
        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Failed to load templates: {(error as Error).message}
            </p>
          </Card>
        )}

        {data && rows.length === 0 && (
          <Card className="text-center py-10">
            <p className="text-sm text-fg-secondary">
              No templates. Click <strong>New template</strong> to define one.
            </p>
          </Card>
        )}

        {rows.length > 0 && (
          <div className="border border-border-subtle rounded-md bg-surface overflow-hidden">
            <table className="w-full text-sm tabular-nums">
              <thead className="border-b border-border-subtle bg-surface-elev">
                <tr className="text-xs uppercase tracking-wider text-fg-tertiary">
                  <th className="text-left px-4 py-2.5 font-medium">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium">Origin</th>
                  <th className="text-right px-4 py-2.5 font-medium">Parameters</th>
                  <th className="text-left px-4 py-2.5 font-medium">Last used</th>
                  <th className="text-right px-4 py-2.5 font-medium">Use count</th>
                  <th className="text-left px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="border-t border-border-subtle hover:bg-surface-elev">
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{t.name}</div>
                      {t.description && (
                        <div className="text-xs text-fg-tertiary mt-0.5 max-w-md truncate">
                          {t.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {t.origin === 'builtin' ? (
                        <Badge variant="accent" className="text-[10px] uppercase tracking-wider">
                          <Lock className="size-3 mr-1 inline" />
                          builtin
                        </Badge>
                      ) : (
                        <Badge variant="neutral" className="text-[10px] uppercase tracking-wider">
                          custom
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">{t.parameters.length}</td>
                    <td className="px-4 py-2.5 text-xs text-fg-tertiary">
                      {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">{t.useCount}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <a
                          href={`/?templateId=${t.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-accent-fg hover:underline"
                          title="Open the search page with this template selected"
                        >
                          <Play className="size-3.5" />
                          Run
                        </a>
                        {t.origin === 'custom' && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label="Edit"
                            onClick={() => setModal({ kind: 'edit', template: t })}
                          >
                            <Edit2 className="size-3.5" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label="Clone"
                          onClick={() => clone.mutate({ id: t.id })}
                          loading={clone.isPending}
                        >
                          <Copy className="size-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label="Archive"
                          onClick={() => {
                            if (window.confirm(`Archive "${t.name}"?`)) {
                              archive.mutate(t.id);
                            }
                          }}
                          loading={archive.isPending}
                        >
                          <Archive className="size-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Dialog open={modal !== null} onOpenChange={(open) => !open && setModal(null)}>
        <DialogContent className="max-w-3xl">
          {modal?.kind === 'create' && <TemplateForm onClose={() => setModal(null)} />}
          {modal?.kind === 'edit' && (
            <TemplateForm template={modal.template} onClose={() => setModal(null)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateForm({
  template,
  onClose,
}: {
  template?: KeepaQueryTemplate;
  onClose: () => void;
}) {
  const isEdit = Boolean(template);
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [fixedFiltersJson, setFixedFiltersJson] = useState(
    JSON.stringify(template?.fixedFilters ?? { limit: 25, rationale: 'New template' }, null, 2)
  );
  const [parametersJson, setParametersJson] = useState(
    JSON.stringify(template?.parameters ?? [], null, 2)
  );
  const [error, setError] = useState<string | null>(null);

  const create = useCreateKeepaTemplate();
  const update = useUpdateKeepaTemplate(template?.id ?? '');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    let fixedFilters: Record<string, unknown>;
    let parameters: unknown;
    try {
      fixedFilters = JSON.parse(fixedFiltersJson);
    } catch (err) {
      setError(`fixedFilters JSON parse error: ${(err as Error).message}`);
      return;
    }
    try {
      parameters = JSON.parse(parametersJson);
      if (!Array.isArray(parameters)) {
        throw new Error('parameters must be an array');
      }
    } catch (err) {
      setError(`parameters JSON parse error: ${(err as Error).message}`);
      return;
    }

    try {
      if (isEdit && template) {
        await update.mutateAsync({
          name: name.trim(),
          description: description.trim() || null,
          fixedFilters,
          parameters: parameters as never,
        });
      } else {
        await create.mutateAsync({
          name: name.trim(),
          description: description.trim() || null,
          fixedFilters,
          parameters: parameters as never,
        });
      }
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Edit template' : 'New template'}</DialogTitle>
      </DialogHeader>

      <div className="space-y-1.5">
        <Label htmlFor="tpl-name">Name</Label>
        <Input
          id="tpl-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Replenishable BSR Sweet Spot"
          required
          maxLength={120}
        />
      </div>

      {/*
        Read-only "Original query" provenance row. Surfaces the NL text the
        operator typed when they saved this template from SearchPage. Hidden
        for templates created via this raw-JSON form (no NL source). Editing
        the NL post-save is deferred (would need re-translation logic).
      */}
      {template?.naturalLanguageQuery && (
        <div className="space-y-1.5">
          <Label>Original query</Label>
          <p className="text-sm text-fg-secondary italic whitespace-pre-wrap rounded-sm border border-border-subtle bg-surface-elev px-3 py-2">
            {template.naturalLanguageQuery}
          </p>
          <p className="text-xs text-fg-tertiary">
            The natural-language query that was translated into the filters below. Read-only — to
            change it, clone this template and save a new version.
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="tpl-description">Description (optional)</Label>
        <Input
          id="tpl-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tpl-fixed">fixedFilters (partial KeepaFilters JSON)</Label>
        <textarea
          id="tpl-fixed"
          value={fixedFiltersJson}
          onChange={(e) => setFixedFiltersJson(e.target.value)}
          rows={8}
          className="w-full px-3 py-2 bg-surface border border-border-default rounded-sm font-mono text-xs"
        />
        <p className="text-xs text-fg-tertiary">
          The non-swappable part of the query. Parameter paths from the array below override values
          here.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tpl-parameters">parameters (TemplateParameterSpec[])</Label>
        <textarea
          id="tpl-parameters"
          value={parametersJson}
          onChange={(e) => setParametersJson(e.target.value)}
          rows={10}
          className="w-full px-3 py-2 bg-surface border border-border-default rounded-sm font-mono text-xs"
        />
        <p className="text-xs text-fg-tertiary">
          Each entry: {`{ path, label, kind, defaultValue?, required?, options?, helpText? }`}. Max
          10.
        </p>
      </div>

      {error && <p className="text-sm text-negative">{error}</p>}

      <DialogFooter>
        <DialogClose asChild>
          <Button variant="ghost" type="button">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" loading={create.isPending || update.isPending}>
          {isEdit ? 'Save changes' : 'Create template'}
        </Button>
      </DialogFooter>
    </form>
  );
}
